import { type ReactNode } from 'react';
import { AuditDataProvider, useOptionalAuditBatches } from '../AuditDataSource';
import { mockAuditSource } from '../sources/mockAuditSource';
import { useAuditTour } from './TourProvider';

// Example data for the walkthrough, when the reviewer's own queue is empty.
//
// # Why this is needed at all
//
// The tour crosses four screens — queue, a batch, a case review, the dashboard —
// and eight of its twelve steps spotlight an element that only exists once
// there is a batch to open. A reviewer signing in for the first time usually
// has nothing assigned yet, which is exactly when the tour fires. Without this
// the steps render as centred cards narrating screens the reviewer never sees.
//
// # Why it is gated on BOTH conditions
//
// Only while the tour is open, and only when the live queue is empty.
//
// Showing example batches to a reviewer who simply has no work assigned would
// be worse than showing nothing: an empty queue and a queue full of fictitious
// work are different situations, and the second one gets acted on. And a
// reviewer who DOES have batches should be walked through their own — the tour
// is about their work, and demonstrating on invented cases while real ones sit
// behind the overlay is a strange thing to do.
//
// # How it overrides
//
// By nesting a second AuditDataProvider, which shadows the outer one for
// everything below. That keeps the swap in one place instead of teaching each
// of the three consuming hooks about the tour, and it means the batch, the case
// list and the case review all come from the same source — a tour that opened a
// demo batch and then a live case would break at the third step.

/** Renders children against the demo source while the tour needs one. */
export function TourDemoData({ children }: { children: ReactNode }) {
  const tour = useAuditTour();
  // The OPTIONAL hook: WorkbenchShell is also mounted without an audit provider
  // (tests, the backendless preview), and a help feature must never be the
  // reason a shell fails to render. The throwing hook took five shell tests
  // down with it.
  const live = useOptionalAuditBatches();

  // Only substitute once the live queue has actually ANSWERED and answered
  // empty. Substituting while it loads would flash demo batches into a queue
  // that was about to have real ones.
  const liveIsEmpty = live?.status === 'success' && (live.data?.length ?? 0) === 0;
  const useDemo = Boolean(tour?.isOpen) && liveIsEmpty;

  if (!useDemo) return <>{children}</>;
  // Its own cache scope. Without it every hook below keys identically to the
  // live provider's and is handed the cached EMPTY answer, so the substitution
  // silently does nothing — which is how this behaved when first written.
  return (
    <AuditDataProvider source={mockAuditSource} scope="tour-demo">
      {children}
    </AuditDataProvider>
  );
}

/** Whether the reviewer is currently looking at example data. Drives the banner. */
export function useIsTourDemoData(): boolean {
  const tour = useAuditTour();
  const live = useOptionalAuditBatches();
  return Boolean(tour?.isOpen) && live?.status === 'success' && (live.data?.length ?? 0) === 0;
}
